import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Telescope, Heart, Eye, TrendingUp, Clock, Filter } from "lucide-react";
import axios from "axios";
import { useAuth } from "@/lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INSTRUMENT_LABEL = {
  vespera: "Vespera",
  iphone: "iPhone",
  apn: "APN",
  telescope: "Télescope",
  camera_astro: "Caméra astro",
  inconnu: "Autre",
};

const OBJECT_LABEL = {
  galaxie: "Galaxie",
  nebuleuse_emission: "Nébuleuse",
  nebuleuse_reflexion: "Nébuleuse réflexion",
  amas_ouvert: "Amas ouvert",
  amas_globulaire: "Amas globulaire",
  voie_lactee: "Voie lactée",
  lune: "Lune",
  soleil: "Soleil",
  champ_large: "Champ large",
  planetaire: "Planétaire",
};


export default function Gallery() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [facets, setFacets] = useState({ instruments: [], objects: [], total: 0 });
  const [instrument, setInstrument] = useState(null);
  const [objectType, setObjectType] = useState(null);
  const [sort, setSort] = useState("recent");
  const [loading, setLoading] = useState(true);

  useEffect(() => { axios.get(`${API}/gallery/facets`).then((r) => setFacets(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    setLoading(true);
    const params = { sort };
    if (instrument) params.instrument = instrument;
    if (objectType) params.object_type = objectType;
    axios.get(`${API}/gallery`, { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, [instrument, objectType, sort]);

  return (
    <div className="min-h-screen relative">
      <header className="sticky top-0 z-40 glass border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="gallery-home-link">
            <div className="w-9 h-9 rounded-md border border-white/10 grid place-items-center bg-gradient-to-br from-violet-500/30 to-blue-500/10">
              <Telescope className="w-4 h-4 text-violet-200" strokeWidth={1.5} />
            </div>
            <div className="font-medium tracking-tight">AstroLift Gallery</div>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            {user ? (
              <Link to="/" className="text-violet-300 hover:underline" data-testid="gallery-to-studio">Studio →</Link>
            ) : (
              <Link to="/pricing" className="text-violet-300 hover:underline" data-testid="gallery-cta-pricing">Sublimer vos images →</Link>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10 space-y-8">
        <div>
          <div className="label-eyebrow inline-flex items-center gap-2"><Filter className="w-3 h-3" /> Communauté</div>
          <h1 className="mt-2 text-4xl sm:text-5xl font-light tracking-tighter">Galerie publique</h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">
            Les meilleures astrophotos sublimées par AstroLift. Vespera, iPhone, APN, télescopes, caméras astro.
            <span className="ml-2 font-mono">{facets.total} image{facets.total > 1 ? "s" : ""}</span>
          </p>
        </div>

        <div className="flex flex-wrap gap-6">
          <div>
            <div className="label-eyebrow mb-2">Tri</div>
            <div className="flex gap-2">
              <button onClick={() => setSort("recent")} data-testid="sort-recent" className={`px-3 py-1.5 rounded-md text-xs border inline-flex items-center gap-1.5 ${sort === "recent" ? "border-primary bg-primary/15 text-white" : "border-white/10 text-muted-foreground"}`}>
                <Clock className="w-3 h-3" /> Récent
              </button>
              <button onClick={() => setSort("popular")} data-testid="sort-popular" className={`px-3 py-1.5 rounded-md text-xs border inline-flex items-center gap-1.5 ${sort === "popular" ? "border-primary bg-primary/15 text-white" : "border-white/10 text-muted-foreground"}`}>
                <TrendingUp className="w-3 h-3" /> Populaire
              </button>
            </div>
          </div>
          <div className="flex-1 min-w-[14rem]">
            <div className="label-eyebrow mb-2">Instrument</div>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setInstrument(null)} data-testid="instrument-all" className={`px-2.5 py-1 rounded-md text-[11px] border ${!instrument ? "border-primary bg-primary/15" : "border-white/10 text-muted-foreground"}`}>Tous</button>
              {facets.instruments.map((f) => (
                <button key={f.key} onClick={() => setInstrument(instrument === f.key ? null : f.key)} data-testid={`instrument-${f.key}`} className={`px-2.5 py-1 rounded-md text-[11px] border ${instrument === f.key ? "border-primary bg-primary/15" : "border-white/10 text-muted-foreground"}`}>
                  {INSTRUMENT_LABEL[f.key] || f.key} <span className="text-muted-foreground/60">·{f.count}</span>
                </button>
              ))}
            </div>
          </div>
          <div className="flex-1 min-w-[14rem]">
            <div className="label-eyebrow mb-2">Type d'objet</div>
            <div className="flex flex-wrap gap-1.5">
              <button onClick={() => setObjectType(null)} data-testid="object-all" className={`px-2.5 py-1 rounded-md text-[11px] border ${!objectType ? "border-primary bg-primary/15" : "border-white/10 text-muted-foreground"}`}>Tous</button>
              {facets.objects.map((f) => (
                <button key={f.key} onClick={() => setObjectType(objectType === f.key ? null : f.key)} data-testid={`object-${f.key}`} className={`px-2.5 py-1 rounded-md text-[11px] border ${objectType === f.key ? "border-primary bg-primary/15" : "border-white/10 text-muted-foreground"}`}>
                  {OBJECT_LABEL[f.key] || f.key} <span className="text-muted-foreground/60">·{f.count}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {loading ? (
          <div className="grid place-items-center py-20"><div className="orbital" /></div>
        ) : items.length === 0 ? (
          <div className="rounded-md border border-dashed border-white/10 p-16 text-center text-muted-foreground" data-testid="gallery-empty">
            Aucune image pour ce filtre. <Link to={user ? "/" : "/signup"} className="text-violet-300 underline-offset-2 hover:underline">Soyez le premier à publier.</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5" data-testid="gallery-grid">
            {items.map((it) => (
              <GalleryCard key={it.id} item={it} />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}


function GalleryCard({ item }) {
  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(item.likes || 0);
  const r = item.report || {};
  const src = `${API}/share/${item.share_token}/file/processed`;
  const onLike = async (e) => {
    e.preventDefault();
    const headers = {};
    const token = localStorage.getItem("astrolift_token");
    if (token) headers.Authorization = `Bearer ${token}`;
    const { data } = await axios.post(`${API}/gallery/${item.share_token}/like`, {}, { headers });
    setLiked(data.liked);
    setLikes(data.likes);
  };
  return (
    <Link to={`/share/${item.share_token}`} className="group rounded-md border border-white/10 bg-card overflow-hidden hover:-translate-y-[2px] transition-transform" data-testid={`gallery-card-${item.id}`}>
      <div className="relative aspect-[4/3] bg-black">
        <img src={src} alt={item.name} className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded bg-black/60 backdrop-blur text-[10px] font-mono">{r.global_score}/100</div>
        <div className="absolute bottom-2 right-2 flex items-center gap-1 text-[10px] font-mono">
          <span className="px-1.5 py-0.5 rounded bg-black/60 backdrop-blur inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {item.views || 0}</span>
          <button onClick={onLike} data-testid={`like-${item.id}`} className={`px-1.5 py-0.5 rounded backdrop-blur inline-flex items-center gap-1 transition-colors ${liked ? "bg-pink-500/40 text-pink-100" : "bg-black/60 hover:bg-pink-500/30"}`}>
            <Heart className={`w-3 h-3 ${liked ? "fill-current" : ""}`} /> {likes}
          </button>
        </div>
      </div>
      <div className="p-3">
        <div className="text-sm font-medium truncate">{item.name}</div>
        <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground">
          <span>{r.classification_label || OBJECT_LABEL[item.classification] || "—"}</span>
          <span className="font-mono">@{item.author_handle || "anonyme"}</span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {(item.tags || []).slice(0, 3).map((t) => (
            <span key={t} className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-white/10 text-muted-foreground">{t}</span>
          ))}
        </div>
      </div>
    </Link>
  );
}
